import AgoraUIKit from 'amhm-vc-ui-kit'
import React from 'react'
import { LSPageImmediateVideoCallContainer } from 'src/consumer/components/LiveSessionPage/LiveSessionPageStyles'
import CustomVideoPlaceholder from 'src/consumer/components/LiveSessionPage/components/VideoCall/CustomVideoPlaceholder'
import 'src/consumer/components/LiveSessionPage/VideoCallEnhancedStyles.css' // v2.1
// AUDIO MANAGER: Shared singleton for meeting sounds - preloaded and cached
import { audioManager } from 'src/consumer/utils/audioManager'

const ToggleState = {
  disabled: 0, // set as 0 - to evaluate falsy
  enabled: 1, // set as 1 - to evaluate truthy
  disabling: 2, // enabled -> disabling -> disabled
  enabling: 3, // disabled -> enabling -> enabled
}

export default function AgoraVideoComponent({
  agoraRtcProps,
  agoraRtmProps,
  handleLeaveRoom,
  onUserJoin,
  onUserLeave,
  isFullHeight,
  onCameraClose,
  onCameraOpen,
  onAudioClose,
  onAudioOpen,
  callContainerStyles = {},
  agoraKitStyleProps = {},
  playAudioOnUserJoin = true,
  playAudioOnUserLeave = true,
  onUsernameMapUpdate,
}) {
  // Debug logging removed to prevent continuous logging loops

  // Store username mapping ref
  const usernameMapRef = React.useRef({})
  
  // Store username map as state for CustomVideoPlaceholder
  const [uidToUsernameMap, setUidToUsernameMap] = React.useState({})
  
  // AUDIO: Using shared audioManager - audio is preloaded by parent component
  // No need to create Audio objects here, audioManager handles caching
  
  // Track played users to prevent duplicate audio playback
  const playedUsersRef = React.useRef(new Set())
  
  // Memoize callbacks to prevent recreation on every render
  const agoraCallbacks = React.useMemo(() => ({
    EndCall: () => handleLeaveRoom(),
    'user-joined': (user) => {
      // Debug logging removed to prevent continuous logging loops
      
      const userKey = `join-${user?.uid}`
      if (playAudioOnUserJoin && !playedUsersRef.current.has(userKey)) {
        playedUsersRef.current.add(userKey)
        // Use shared audioManager for cached, non-blocking audio playback
        audioManager.playJoinSound()
        // Remove from set after a delay to allow re-playing if user rejoins
        setTimeout(() => {
          playedUsersRef.current.delete(userKey)
        }, 1000)
      }
      
      // CRITICAL: Call onUserJoin callback if provided
      if (onUserJoin) {
        onUserJoin(user)
      }
    },
    'user-left': (user) => {
      const userKey = `leave-${user?.uid}`
      if (playAudioOnUserLeave && !playedUsersRef.current.has(userKey)) {
        playedUsersRef.current.add(userKey)
        // Use shared audioManager for cached, non-blocking audio playback
        audioManager.playLeaveSound()
        // Remove from set after a delay to allow re-playing if user leaves again
        setTimeout(() => {
          playedUsersRef.current.delete(userKey)
        }, 1000)
      }
      onUserLeave && onUserLeave(user)
    },
    // Capture RTM username updates
    'rtm-user-joined': (uid, username) => {
      // Store with both string and number keys to ensure we can find it regardless of format
      const uidNum = typeof uid === 'string' ? parseInt(uid) : uid
      const uidStr = String(uid)
      
      // Debug logging removed to prevent continuous logging loops
      
      usernameMapRef.current[uid] = username
      usernameMapRef.current[uidNum] = username
      usernameMapRef.current[uidStr] = username
      
      const updatedMap = { ...usernameMapRef.current }
      
      // Update state for CustomVideoPlaceholder
      setUidToUsernameMap(updatedMap)
      
      onUsernameMapUpdate && onUsernameMapUpdate(updatedMap)
    },
    'rtm-user-left': (uid) => {
      // Remove from all possible key formats
      const uidNum = typeof uid === 'string' ? parseInt(uid) : uid
      const uidStr = String(uid)
      
      delete usernameMapRef.current[uid]
      delete usernameMapRef.current[uidNum]
      delete usernameMapRef.current[uidStr]
      
      const updatedMap = { ...usernameMapRef.current }
      
      // Update state for CustomVideoPlaceholder
      setUidToUsernameMap(updatedMap)
      
      onUsernameMapUpdate && onUsernameMapUpdate(updatedMap)
    },
    'local-user-mute-video': (state) => {
      if (state === ToggleState.disabling) {
        onCameraClose && onCameraClose()
      } else if (state === ToggleState.enabling) {
        onCameraOpen && onCameraOpen()
      }
    },
    'local-user-mute-audio': (state) => {
      if (state === ToggleState.disabling) {
        onAudioClose && onAudioClose()
      } else if (state === ToggleState.enabling) {
        onAudioOpen && onAudioOpen()
      }
    },
  }), [
    handleLeaveRoom,
    playAudioOnUserJoin,
    playAudioOnUserLeave,
    onUserJoin,
    onUserLeave,
    onUsernameMapUpdate,
    onCameraClose,
    onCameraOpen,
    onAudioClose,
    onAudioOpen,
  ])
  
  // Store local username
  React.useEffect(() => {
    if (agoraRtcProps?.uid !== undefined && agoraRtmProps?.username) {
      const uid = agoraRtcProps.uid
      const uidNum = typeof uid === 'string' ? parseInt(uid) : uid
      const uidStr = String(uid)
      
      // Debug logging removed to prevent continuous logging loops
      
      // Store with all key formats
      usernameMapRef.current[uid] = agoraRtmProps.username
      usernameMapRef.current[uidNum] = agoraRtmProps.username
      usernameMapRef.current[uidStr] = agoraRtmProps.username
      
      const updatedMap = { ...usernameMapRef.current }
      
      // Update state for CustomVideoPlaceholder
      setUidToUsernameMap(updatedMap)
      
      onUsernameMapUpdate && onUsernameMapUpdate(updatedMap)
    }
  }, [agoraRtcProps?.uid, agoraRtmProps?.username, onUsernameMapUpdate])
  
  // Create memoized CustomVideoPlaceholder wrapper that includes current state
  const customVideoPlaceholderWrapper = React.useMemo(() => {
    return (props) => (
      <CustomVideoPlaceholder
        {...props}
        uidToUsernameMap={uidToUsernameMap}
        localRtmUsername={agoraRtmProps?.username}
      />
    )
  }, [uidToUsernameMap, agoraRtmProps?.username])

  // Ensure buttons are clickable and visible - minimal intervention, let Agora handle positioning
  React.useEffect(() => {
    // Ensure buttons are clickable and visible - Agora handles positioning via styleProps.localBtnContainer
    const makeButtonsClickableAndVisible = () => {
      const container = document.querySelector('[data-video-container="left-frame"]') ||
                        document.querySelector('.agora-video-container') || 
                        document.querySelector('.LSPageImmediateVideoCallContainer')
      
      if (!container) return
      
      // Find button container (flex container with buttons)
      const buttonContainers = container.querySelectorAll('div[style*="display: flex"]')
      buttonContainers.forEach((btnContainer) => {
        const hasButtons = btnContainer.querySelectorAll('button').length > 0
        if (hasButtons) {
          // Force visibility and positioning
          btnContainer.style.setProperty('visibility', 'visible', 'important')
          btnContainer.style.setProperty('opacity', '1', 'important')
          btnContainer.style.setProperty('display', 'flex', 'important')
          btnContainer.style.setProperty('pointer-events', 'auto', 'important')
          btnContainer.style.setProperty('z-index', '10000', 'important')
          btnContainer.style.setProperty('overflow', 'visible', 'important')
          btnContainer.style.setProperty('clip-path', 'none', 'important')
          
          // Ensure positioning if not already set
          const computedPosition = window.getComputedStyle(btnContainer).position
          if (computedPosition === 'static' || !computedPosition) {
            btnContainer.style.setProperty('position', 'absolute', 'important')
            btnContainer.style.setProperty('bottom', '24px', 'important')
            btnContainer.style.setProperty('left', '50%', 'important')
            btnContainer.style.setProperty('transform', 'translateX(-50%)', 'important')
          }
        }
      })
      
      // Ensure buttons are clickable
      const buttons = container.querySelectorAll('button')
      buttons.forEach((button) => {
        if (!container.contains(button)) return
        
        // Ensure buttons are clickable and visible
        button.style.setProperty('pointer-events', 'auto', 'important')
        button.style.setProperty('touch-action', 'manipulation', 'important')
        button.style.setProperty('cursor', 'pointer', 'important')
        button.style.setProperty('visibility', 'visible', 'important')
        button.style.setProperty('opacity', '1', 'important')
        button.style.setProperty('z-index', '10001', 'important')
      })
    }

    // Run a few times to catch dynamically rendered buttons
    makeButtonsClickableAndVisible()
    const timeout1 = setTimeout(makeButtonsClickableAndVisible, 100)
    const timeout2 = setTimeout(makeButtonsClickableAndVisible, 300)
    const timeout3 = setTimeout(makeButtonsClickableAndVisible, 500)
    const timeout4 = setTimeout(makeButtonsClickableAndVisible, 1000)
    const timeout5 = setTimeout(makeButtonsClickableAndVisible, 2000)

    // Also run periodically to catch any late-rendered buttons
    const interval = setInterval(makeButtonsClickableAndVisible, 2000)

    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
      clearTimeout(timeout3)
      clearTimeout(timeout4)
      clearTimeout(timeout5)
      clearInterval(interval)
    }
  }, [])

  return (
    <LSPageImmediateVideoCallContainer
      className="hide-on-print-only agora-video-container"
      isFullHeight={isFullHeight}
      style={{
        ...callContainerStyles,
        // Use 100% height when inside a container, 100vh when full height
        height: isFullHeight ? '100vh' : '100%',
        maxHeight: isFullHeight ? '100vh' : '100%',
        minHeight: 0, // Allow flexbox to shrink
        overflow: 'hidden', // Prevent scrolling - video cards should fit within viewport
        position: 'relative',
        // Ensure video fits within parent container without scrolling
        flex: isFullHeight ? undefined : '1 1 0', // Take available space when in container
        width: '100%', // Ensure full width
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AgoraUIKit
        rtcProps={{
          ...agoraRtcProps,
          // Beautiful custom placeholder when camera is off
          CustomVideoPlaceholder: customVideoPlaceholderWrapper,
        }}
        callbacks={agoraCallbacks}
        styleProps={{
          // ✨ Enhanced Modern Glass Effect Button Container - Mobile Optimized
          // Agora handles positioning in the main video frame automatically
          localBtnContainer: {
            padding: window.innerWidth <= 768 ? '12px 14px' : '10px 16px',
            gap: window.innerWidth <= 768 ? '12px' : '10px',
            // Glass morphism effect - very transparent to blend with background
            background: 'rgba(255, 255, 255, 0.05)', // Very transparent white (almost invisible)
            backdropFilter: 'blur(16px) saturate(150%)',
            WebkitBackdropFilter: 'blur(16px) saturate(150%)',
            borderRadius: window.innerWidth <= 768 ? '24px' : '28px',
            border: '1px solid rgba(255, 255, 255, 0.15)', // Very subtle border
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.02), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
            width: 'fit-content',
            minWidth: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            // On mobile: position fixed to overlay video and ensure visibility
            // On desktop: position absolute within video container
            position: window.innerWidth <= 768 ? 'fixed' : 'absolute',
            bottom: window.innerWidth <= 768 ? '40px' : '24px', // Increased bottom spacing on mobile to prevent clipping
            left: window.innerWidth <= 768 ? '50%' : '50%',
            transform: 'translateX(-50%)',
            zIndex: window.innerWidth <= 768 ? 10002 : 10000, // Higher z-index on mobile to ensure it's in front
            pointerEvents: 'auto', // Allow clicks on container and buttons
            // On mobile, ensure controls are always visible and not cut off
            ...(window.innerWidth <= 768 ? {
              width: 'calc(100% - 40px)', // Leave more margin on sides
              maxWidth: '600px', // Don't make it too wide
              margin: '0 auto', // Center it
              paddingBottom: 'env(safe-area-inset-bottom, 0px)', // Account for iOS safe area
            } : {})
          },
          // 🎨 Modern Individual Button Styling - Larger on Mobile
          localBtnStyles: {
            width: window.innerWidth <= 768 ? '52px' : '48px',
            height: window.innerWidth <= 768 ? '52px' : '48px',
            minWidth: window.innerWidth <= 768 ? '52px' : '48px',
            minHeight: window.innerWidth <= 768 ? '52px' : '48px',
            maxWidth: window.innerWidth <= 768 ? '52px' : '48px',
            maxHeight: window.innerWidth <= 768 ? '52px' : '48px',
            borderRadius: window.innerWidth <= 768 ? '14px' : '12px', // Soft rounded corners (not circle)
            margin: '0',
            padding: '0',
            boxSizing: 'border-box', // Include border in width/height calculation
            // Ensure background is always visible
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
            backgroundColor: 'rgba(255, 255, 255, 0.2)', // Fallback background
            border: '1.5px solid rgba(255, 255, 255, 0.35)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2), 0 1px 4px rgba(0, 0, 0, 0.1), inset 0 1px 1px rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            pointerEvents: 'auto', // Buttons must capture clicks
            zIndex: 10001, // Higher than container
            position: 'relative',
            // Prevent distortion
            flexShrink: 0,
            flexGrow: 0,
            overflow: 'hidden', // Prevent content overflow
          },
          // 🔴 End Call Button (larger, prominent red gradient) - Even Larger on Mobile
          endCall: {
            width: window.innerWidth <= 768 ? '56px' : '52px',
            height: window.innerWidth <= 768 ? '56px' : '52px',
            minWidth: window.innerWidth <= 768 ? '56px' : '52px',
            minHeight: window.innerWidth <= 768 ? '56px' : '52px',
            maxWidth: window.innerWidth <= 768 ? '56px' : '52px',
            maxHeight: window.innerWidth <= 768 ? '56px' : '52px',
            borderRadius: window.innerWidth <= 768 ? '16px' : '14px', // Soft rounded corners (not circle)
            padding: '0',
            margin: '0',
            boxSizing: 'border-box', // Include border in width/height calculation
            // Ensure background is always visible
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 1) 100%)',
            backgroundColor: 'rgba(239, 68, 68, 0.95)', // Fallback background
            border: '1.5px solid rgba(255, 255, 255, 0.4)',
            boxShadow: '0 6px 24px rgba(239, 68, 68, 0.6), 0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.35)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            pointerEvents: 'auto', // Button must capture clicks
            zIndex: 10001, // Higher than container
            position: 'relative',
            cursor: 'pointer',
            // Prevent distortion
            flexShrink: 0,
            flexGrow: 0,
            overflow: 'hidden', // Prevent content overflow
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          ...(agoraKitStyleProps || {}),
          // Enhanced modern styling with white background
          // position: relative is required so localBtnContainer with position: absolute positions relative to this container
          UIKitContainer: {
            background: '#ffffff',
            border: 'none',
            boxShadow: 'none',
            height: '100%',
            maxHeight: '100%',
            minHeight: 0, // Allow flexbox to shrink
            overflow: 'hidden', // Prevent scrolling - video cards should fit within container
            position: 'relative', // Required for localBtnContainer absolute positioning to work correctly
            width: '100%', // Ensure it takes full width of parent
            display: 'flex',
            flexDirection: 'column',
            ...(agoraKitStyleProps?.UIKitContainer || {}),
          },
          // Beautiful username display on video - purple/blue glass theme
          usernameText: {
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.85) 0%, rgba(99, 102, 241, 0.85) 100%)',
            backdropFilter: 'blur(12px) saturate(180%)',
            WebkitBackdropFilter: 'blur(12px) saturate(180%)',
            color: '#ffffff',
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px 16px',
            borderRadius: '16px',
            boxShadow: '0 4px 16px rgba(139, 92, 246, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
            letterSpacing: '0.3px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            // Position at top-left with flexible width
            position: 'absolute',
            top: '12px',
            left: '12px',
            right: 'auto',
            margin: '0',
            // Flexible width
            width: 'auto',
            maxWidth: 'calc(100% - 24px)',
            minWidth: 'fit-content',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ...(agoraKitStyleProps?.usernameText || {}),
          },
        }}
        rtmProps={agoraRtmProps}
      />
    </LSPageImmediateVideoCallContainer>
  )
}
